let module = angular.module('boxcast.services.facebook', []);

// NOTE: Changing the scope of permissions from Facebook requires
// that you submit the app for approval for the permissions using
// the tools at developer.facebook.com.
const FACEBOOK_SCOPE = [
  'business_management',
  'pages_manage_metadata',
  'pages_manage_posts',
  'pages_read_engagement',
  'pages_show_list',
  'publish_video',
].join(',');

const FB_LOAD_STATUS_DEBOUNCE_MS = 60000; //only poll once per minute at most

module.service('Facebook', FacebookService);

export function FacebookService($http, $q, $timeout) {
  'ngInject';

  this.loading = false;
  this.auth = {};

  this.init = () => {
    this.loading = true;
    return waitForSDK()
      .then(() => {
        return this.loadStatus({ silent: true });
      })
      .then(() => {
        this.loading = false;
      })
      .catch((err) => {
        this.loading = false;
        remoteLogger.error('Unable to initialize Facebook', { error: err });
      });
  };

  this._loadStatusInFlight = false;
  this._lastLoadStatusComplete = 0;
  this.loadStatus = (options) => {
    return $q((resolve, reject) => {
      let checkAgainTimer = $timeout(() => {
        // getLoginStatus sometimes hangs the first time...
        this.loadStatus(options).then(() => {
          resolve(this);
        });
      }, 1000);

      if (this._loadStatusInFlight) {
        console.log('Facebook#loadStatus -- in flight, will not start another FB API call');
        return; //let checkAgainTimer resolve in a bit
      }

      if (
        !options.force &&
        new Date() - this._lastLoadStatusComplete < FB_LOAD_STATUS_DEBOUNCE_MS
      ) {
        console.log('Facebook#loadStatus -- using cached loadStatus value');
        $timeout.cancel(checkAgainTimer);
        resolve(this);
        return;
      }

      this._loadStatusInFlight = true;
      FB.getLoginStatus((response) => {
        if (checkAgainTimer) {
          $timeout.cancel(checkAgainTimer);
        }
        $timeout(() => {
          this._loadStatusInFlight = false;
          this._lastLoadStatusComplete = new Date();
          updateInstanceFromAuthResponse(response, options)
            .then(() => resolve(this))
            .catch((e) => reject(e));
        }, 0);
      }, true);
    });
  };

  const updateInstanceFromAuthResponse = (response, options = {}) => {
    this.connected = response.status == 'connected';
    this.status = response.status;
    this.auth = response.authResponse || {};
    if (this.connected) {
      return $q.all([
        this.queryMe(options),
        this.queryAccounts(),
        this.queryEvents(),
        this.queryGroups(),
      ]);
    } else {
      this.me = {};
      this.accounts = [];
      return $q((r) => r());
    }
  };

  this.queryMe = (options) => {
    return $q((resolve, reject) => {
      FB.api('/me', (response) => {
        // name, id
        $timeout(() => {
          this.me = response;
          if (this.auth.accessToken) {
            this.linkAccount(options)
              .then(() => resolve(response))
              .catch((e) => reject(e));
          } else {
            resolve(response);
          }
        }, 0);
      });
    });
  };

  const doFbApi = (url) => {
    return $q((resolve, reject) => {
      FB.api(url, (response) => {
        $timeout(() => {
          if (response.error) {
            remoteLogger.error(`Error from Facebook URL`, {
              facebook_url: url,
              error: response.error,
            });
            reject(response.error);
          } else {
            resolve(response);
          }
        }, 0);
      });
    });
  };

  // It seems that by default, Facebook returns about 25 results per page, taking 500-900ms to do so (all
  // server-side, not network). In order to prevent unbounded recursion and a long delay when loading the
  // Facebook account, we limit results to 10 pages or 250 results (whichever comes first). In general,
  // this should keep load times below 5-10s (and usually lower, assuming most people don't have _that_
  // many pages, groups, and events).
  const fbRecursiveCallLimit = 10;
  const fbResultLimit = 250;
  const paginateFbApi = (url) => {
    return $q((resolve, reject) => {
      // Closure variable to collect results
      let results = [];
      let totalCalls = 0;

      // Error and success callbacks
      const completionSuccess = (response) => {
        results = results.concat(response.data || []);
        if (response.paging && response.paging.next) {
          // More pages, apparently
          if (totalCalls >= fbRecursiveCallLimit) {
            console.warn(
              `Facebook would provide more results, but we stopped after ${totalCalls} to the API. Results will be limited.`
            );
            resolve(results);
          } else if (results.length >= fbResultLimit) {
            console.warn(
              `Facebook would provide more results, but we stopped after finding ${results.length} results. Results will be limited.`
            );
            resolve(results);
          } else {
            totalCalls += 1;
            doFbApi(response.paging.next)
              .then(completionSuccess)
              .catch((e) => reject(e));
          }
        } else {
          // No more pages, resolve
          resolve(results);
        }
      };

      // Now kick off the first API call
      totalCalls += 1;
      doFbApi(url)
        .then(completionSuccess)
        .catch((e) => reject(e));
    });
  };

  this.queryAccounts = () => {
    return paginateFbApi('/me/accounts?fields=id,name,cover')
      .then((results) => {
        this.accounts = _.sortBy(results, (a) => (a.name || '').toLowerCase());
        return results;
      })
      .catch((error) => {
        this.accounts = [];
        throw error;
      });
  };

  this.queryEvents = () => {
    return paginateFbApi('/me/events?fields=id,name,cover,start_time')
      .then((results) => {
        this.events = _.sortBy(results, (e) => Date.parse(e.start_time));
        return results;
      })
      .catch((error) => {
        this.events = [];
        throw error;
      });
  };

  this.queryGroups = () => {
    return paginateFbApi('/me/groups?fields=id,name,cover')
      .then((results) => {
        this.groups = _.sortBy(results, (g) => (g.name || '').toLowerCase());
        return results;
      })
      .catch((error) => {
        this.groups = [];
        throw error;
      });
  };

  this.logout = () => {
    return $q((resolve, reject) => {
      if (!window.FB) {
        return resolve(false);
      }
      FB.logout(() => {
        this.facebook_account_id = null;
        this.connected = false;
        this.status = {};
        this.auth = {};
        this.me = {};
        this.accounts = [];
        this.events = [];
        this.groups = [];
        resolve(true);
      });
    });
  };

  this.login = () => {
    return $q((resolve, reject) => {
      FB.login(
        (resp) => {
          updateInstanceFromAuthResponse(resp)
            .then(() => {
              resolve(true);
            })
            .catch((err) => {
              reject(err);
            });
        },
        { scope: FACEBOOK_SCOPE }
      );
    });
  };

  this.linkAccount = (options) => {
    return $q((resolve, reject) => {
      $http
        .get(`${API_ROOT}/account/facebook_users/${this.me.id}`)
        .then((response) => {
          // User already exists, extend the token
          $http
            .put(`${API_ROOT}/account/facebook_users/${this.me.id}`, {
              fb_user_access_token: this.auth.accessToken,
              fb_user_name: this.me.name,
            })
            .then((response) => {
              console.log('Updated Facebook->BoxCast linkage:', response.data);
              this.facebook_account_id = response.data.id;
              resolve(this.facebook_account_id);
            })
            .catch((err) => {
              reject(err);
            });
        })
        .catch((err) => {
          if (err.status != 404) {
            remoteLogger.error('Error linking facebook account:', { error: err });
            reject(err);
            return;
          }
          if (options && options.silent) {
            // 404, user doesn't exist in BoxCast DB ... don't auto-create it since
            // we're in silent mode. Wait for user to explicitly want to link.
            this.connected = false;
            this.status = {};
            this.auth = {};
            this.me = {};
            this.accounts = [];
            this.events = [];
            this.groups = [];
            resolve({});
          } else {
            // 404, user doesn't exist in BoxCast DB ... create it!
            $http
              .post(`${API_ROOT}/account/facebook_users`, {
                fb_user_id: this.me.id,
                fb_user_access_token: this.auth.accessToken,
                fb_user_name: this.me.name,
              })
              .then((response) => {
                console.log('Linked Facebook->BoxCast:', response.data);
                // XXX: is there a race condition?
                this.facebook_account_id = response.data.id;
                resolve(this.facebook_account_id);
              })
              .catch((err) => {
                reject(err);
              });
          }
        });
    });
  };

  // Private helpers

  var waitForSDK = (deferred = null) => {
    if (deferred === null) {
      deferred = $q.defer();
    }

    if (window['FB']) {
      deferred.resolve(FB);
      this.FB = FB;
      return deferred.promise;
    } else {
      console.log('FB SDK not yet loaded, checking again');
      $timeout.cancel(this._waitForSDKTimer);
      this._waitForSDKTimer = $timeout(() => waitForSDK(deferred), 100);
      return deferred.promise;
    }
  };
}

export default module;



// WEBPACK FOOTER //
// ./app/services/facebook.js